import { components, PatientId } from "../../../shared/schema/schema";

export type RnSchedulingMapPatient = components["schemas"]["RnSchedulingMapPatient"];

export type RnSchedulingStatus = components["schemas"]["RnSchedulingStatus"];

export const SCHEDULING_STATUS_LABEL: Record<RnSchedulingStatus, string> = {
  VISIT_COMPLETED: "Visit completed",
  RN_SCHEDULED: "RN scheduled",
  REQUIRE_SCHEDULING: "Require Scheduling",
};

export const SCHEDULING_STATUS_COLOR_SCHEME: Record<RnSchedulingStatus, string> = {
  VISIT_COMPLETED: "blue",
  RN_SCHEDULED: "green",
  REQUIRE_SCHEDULING: "red",
};

/** In the order they are shown in the map legend and the status filter. */
export const SCHEDULING_STATUSES: RnSchedulingStatus[] = [
  "REQUIRE_SCHEDULING",
  "RN_SCHEDULED",
  "VISIT_COMPLETED",
];

/** NYC boroughs are counties, but coordinators know them by the borough name. */
const NYC_BOROUGH_BY_COUNTY_NAME: Record<string, string> = {
  "Bronx County": "Bronx",
  "Kings County": "Brooklyn",
  "New York County": "Manhattan",
  "Queens County": "Queens",
  "Richmond County": "Staten Island",
};

export function isNycBoroughCounty(countyName: string): boolean {
  return countyName in NYC_BOROUGH_BY_COUNTY_NAME;
}

/** The borough name for an NYC county, the county name itself for any other region. */
export function getRegionLabel(countyName: string): string {
  return NYC_BOROUGH_BY_COUNTY_NAME[countyName] ?? countyName;
}

/** A patient that has a usable geo location and can therefore be drawn as a pin. */
export type MappableRnSchedulingMapPatient = RnSchedulingMapPatient & {
  location: NonNullable<RnSchedulingMapPatient["location"]>;
};

export function isMappable(
  patient: RnSchedulingMapPatient
): patient is MappableRnSchedulingMapPatient {
  return patient.location !== null;
}

export function getPatientKey(patient: RnSchedulingMapPatient): PatientId {
  return patient.patientId;
}

const EARTH_RADIUS_MILES = 3958.8;

/** Great-circle distance in miles, used to show how far patients are from each other. */
export function distanceInMiles(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / 2) ** 2;

  return EARTH_RADIUS_MILES * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}
